/**
 * buildCoordEditPayload — Lote C2
 *
 * Construye el payload de persistencia de una edición de coordenadas para el
 * Worker /coord-edits (servicio postCoordEdit). Función pura y determinista:
 * separa la lógica de geomBefore / infraNombre / editSource del cableado React
 * de WizardPageV2.handleApplyCoords, lo que permite testearla sin jsdom.
 */
import type { CoordEditPayload } from '../../services/coordEditsService'

/** Subconjunto de ProcessedInfrastructure necesario para el payload. */
export interface CoordEditSourceRecord {
  id: string
  nombre?: string
  xFinal?: number
  yFinal?: number
}

export interface BuildCoordEditPayloadParams {
  infraId: string
  /** Coordenada X UTM30 confirmada. */
  x: number
  /** Coordenada Y UTM30 confirmada. */
  y: number
  /** Origen: 'manual' (formulario) · 'map' (arrastre) · 'assisted' (candidato S1-5a). */
  source: 'manual' | 'map' | 'assisted'
  /** Código INE 5 dígitos del municipio (nivel documento). */
  ine5: string
  /** Registro previo (para geomBefore e infraNombre); puede no existir. */
  prev?: CoordEditSourceRecord
}

/**
 * Devuelve el payload listo para postCoordEdit.
 *
 * - geomBefore es null salvo que el registro previo tenga AMBAS coordenadas.
 * - infraNombre cae a cadena vacía si el registro previo no aporta nombre.
 * - editSource se propaga tal cual ('manual' | 'map' | 'assisted').
 */
export function buildCoordEditPayload(
  params: BuildCoordEditPayloadParams,
): CoordEditPayload {
  const { infraId, x, y, source, ine5, prev } = params

  const geomBefore =
    prev?.xFinal != null && prev?.yFinal != null
      ? { x: prev.xFinal, y: prev.yFinal }
      : null

  return {
    infraId,
    infraNombre: prev?.nombre ?? '',
    ine5,
    geomBefore,
    geomAfter: { x, y },
    editSource: source,
  }
}
